// Tambahkan deklarasi global agar window.initWeather tidak error di TypeScript
declare global {
  interface Window {
    initWeather?: (type: string) => void;
  }
}

import WeatherBackground from '../components/WeatherBackground';
import PlaylistCard from '../components/PlaylistCard';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { getJakartaISOString } from '../utils/dateUtils';

const defaultCity = 'Jakarta';

const playlists = [
	{
		title: 'Rainy Days',
		icon: '🌧️',
		mood: 'rain',
		songs: ['Song A', 'Song B', 'Song C'],
		count: 31,
		duration: '1 hr 45 min',
		link: 'https://open.spotify.com/playlist/6cRzdgr3BNlspxbXb2bSay',
	},
	{
		title: 'Sunshine Vibes',
		icon: '☀️',
		mood: 'clear',
		songs: ['Song D', 'Song E', 'Song F'],
		count: 22,
		duration: '1 hr 17 min',
		link: 'https://open.spotify.com/playlist/2mgw2dg93sTNVhi80DRlsZ',
	},
	{
		title: 'Twilight Echoes',
		icon: '🌙',
		mood: 'night',
		songs: ['Song G', 'Song H', 'Song I'],
		count: 25,
		duration: '1 hr 27 min',
		link: 'https://open.spotify.com/playlist/2Lqmh2mSXfuMkbWwJfSMAY',
	},
	{
		title: 'Inner Bloom',
		icon: '🌱',
		mood: 'bloom',
		songs: ['Song J', 'Song K', 'Song L'],
		count: 24,
		duration: '1 hr 35 min',
		link: 'https://open.spotify.com/playlist/3OE462mdMjOXJr0fb8DVX1',
	},
	{
		title: 'Sunday Service',
		icon: '✨', // Optionally use a cross/glow SVG
		mood: 'sunday',
		songs: ['Song M', 'Song N', 'Song O'],
		count: 24,
		duration: '2 hr 1 min',
		link: 'https://open.spotify.com/playlist/1ks9LciyUAowsj3LRm8JSk',
	},
];

export default function Playlists() {
	const [weather, setWeather] = useState({
		city: defaultCity,
		desc: 'Cerah',
		icon: '☀️',
		mood: 'clear',
	});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		async function fetchLocationAndWeather() {
			// Fetch location+weather from serverless function; always fall back gracefully
			let city = defaultCity;
			let weatherMain = 'Clear';
			try {
				const locRes = await fetch('/api/location');
				if (locRes.ok) {
					const locData = await locRes.json();
					if (locData.city) city = locData.city;
					if (locData.weather) weatherMain = locData.weather;
				} else {
					console.warn('[Playlists] /api/location returned', locRes.status, '— using Jakarta fallback');
				}
			} catch (err) {
				console.warn('[Playlists] /api/location fetch failed — using Jakarta fallback', err);
			}

			// Map weather string to mood/icon
			let mood = 'clear', icon = '☀️';
			const main = weatherMain.toLowerCase();
			if (main.includes('rain')) { mood = 'rain'; icon = '🌧️'; }
			else if (main.includes('cloud')) { mood = 'cloudy'; icon = '☁️'; }
			else if (main.includes('clear')) { mood = 'clear'; icon = '☀️'; }
			else if (main.includes('snow')) { mood = 'snow'; icon = '❄️'; }
			else if (main.includes('thunder')) { mood = 'thunder'; icon = '⛈️'; }
			else if (main.includes('mist') || main.includes('fog')) { mood = 'fog'; icon = '🌫️'; }

			setWeather({ city, desc: weatherMain, icon, mood });
			setError(null);
			setLoading(false);

			// Insert playlist Rainy Days ke DB jika belum ada
			try {
				const rainPlaylist = playlists.find(p => p.title === 'Rainy Days');
				if (rainPlaylist) {
					const { data: existing } = await supabase
						.from('myplaylist')
						.select('id')
						.eq('link', rainPlaylist.link)
						.single();
					if (!existing) {
						await supabase.from('myplaylist').insert({
							name: rainPlaylist.title,
							link: rainPlaylist.link,
							visits: {},
						});
					}
				}
			} catch {
				// DB insert failure is non-critical, ignore
			}
		}
		fetchLocationAndWeather();
	}, []);

	useEffect(() => {
		if (typeof window !== 'undefined' && window.initWeather && document.getElementById('vanta-bg')) {
			let type = 'Sunny';
			if (weather.mood === 'rain') type = 'Rain';
			else if (weather.mood === 'snow') type = 'Snow';
			else if (weather.mood === 'fog') type = 'Fog/Mist';
			else if (weather.mood === 'cloudy') type = 'Clouds';
			else if (weather.mood === 'thunder' || weather.mood === 'extreme') type = 'Storm/Extreme';
			else if (weather.mood === 'night') type = 'Night';
			else if (weather.mood === 'clear') type = 'Sunny';
			window.initWeather(type);
		}
	}, [weather.mood]);

	// Analytics update logic for playlist clicks
	const updatePlaylistAnalytics = async (playlist: typeof playlists[0]) => {
		try {
			// Fetch IP/city persis seperti WishDetails/StoryDetails
			let ipData: { ip: string; city: string } = { ip: 'unknown', city: 'unknown' };
			try {
				const response = await fetch('https://ipapi.co/json/');
				const json = await response.json();
				if (json.ip && json.city) ipData = { ip: json.ip, city: json.city };
			} catch (e) {
				ipData = { ip: 'unknown', city: 'unknown' };
			}

			// Fallback: jika di localhost, update langsung ke Supabase
			if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
				// Import supabase dinamis agar tidak error SSR
				const { supabase } = await import('../lib/supabase');
				const { data: current, error: fetchError } = await supabase
					.from('myplaylist')
					.select('id, visits')
					.eq('link', playlist.link)
					.single();
				if (fetchError || !current) return;
				const visits = current.visits || {};
				const now = getJakartaISOString();
				const updatedVisits = {
					...visits,
					[ipData.ip]: {
						ip: ipData.ip,
						city: ipData.city,
						count: ((visits?.[ipData.ip]?.count || 0) + 1),
						last_visit: now,
						cities: [...new Set([...(visits?.[ipData.ip]?.cities || []), ipData.city])]
					}
				};
				await supabase
					.from('myplaylist')
					.update({ visits: updatedVisits, lastupdatetime: now })
					.eq('id', current.id);
				return;
			}

			// Production: kirim analytics ke Netlify Function
			await fetch('/.netlify/functions/playlistAnalytics', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					link: playlist.link,
					title: playlist.title,
					ip: ipData.ip,
					city: ipData.city,
					timestamp: getJakartaISOString(),
				}),
			});
		} catch (err) {
			// Silent fail
		}
	};

	if (loading)
		return (
			<div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-blue-200 via-blue-100 to-white dark:from-[#181825] dark:via-[#232634] dark:to-[#181825]">
				Memuat data cuaca...
			</div>
		);
	if (error)
		return (
			<div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-blue-200 via-blue-100 to-white dark:from-[#181825] dark:via-[#232634] dark:to-[#181825]">
				<div className="text-2xl font-bold text-center mb-4 text-[#232634] dark:text-[#f9e2af]">{error}</div>
				<button
					onClick={() => {
						setLoading(true);
						setError(null);
					}}
					className="px-4 py-2 rounded bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 transition-colors"
				>
					Coba Lagi
				</button>
			</div>
		);
	return (
		<>
			{/* Vanta.js Clouds background hanya untuk mood cloudy */}
			{weather.mood === 'cloudy' && (
				<div
					id="vanta-bg"
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						width: '100%',
						height: '100vh',
						zIndex: -1,
					}}
				/>
			)}
			<div className="relative min-h-screen w-full weather-container" style={{zIndex: 1}}>
				{/* Atmosfer Layer */}
				<div className="pointer-events-none absolute inset-0 z-0" style={{background: 'radial-gradient(circle at top right, rgba(255,255,255,0.3), transparent)'}} />
				{/* Cloud Layer (hanya untuk clear/cloudy) */}
				{(weather.mood === 'clear' || weather.mood === 'cloudy') && (
					<div className="cloud" style={{position:'absolute',top:0,left:0,width:'140vw',height:200,background:'url(/clouds.svg) no-repeat',backgroundSize:'cover',opacity:0.3,animation:'cloudDrift 60s linear infinite',zIndex:0}} />
				)}
				{/* Sun & Sun Glow (hanya untuk clear) */}
				{weather.mood === 'clear' && (
					<>
						<div className="sun-glow" style={{position:'absolute',top:80,right:80,width:120,height:120,background:'radial-gradient(circle, rgba(253,224,71,0.3), transparent)',zIndex:0,pointerEvents:'none',animation:'glowPulse 6s ease-in-out infinite'}} />
						<div className="sun" style={{position:'absolute',top:50,right:80,zIndex:1,animation:'sunPeek 10s ease-in-out infinite'}}>
							<div style={{width:80,height:80,borderRadius:'50%',background:'#fde047',boxShadow:'0 0 60px 10px #fde04788'}} />
						</div>
					</>
				)}
				{/* Weather Animation (Lottie) - kecil, kanan bawah badge */}
				<div className="absolute z-10" style={{top:110, right:'12%', width:180, height:110, minWidth:140}}>
					<WeatherBackground mood={weather.mood} />
				</div>
				<div className="relative z-20 flex flex-col items-center pt-10 md:pt-20 pb-10 px-2 md:px-0 min-h-[60vh]">
					<h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-center drop-shadow-lg animate-fadeIn font-display tracking-tight text-[#232634] dark:text-[#f9e2af]" style={{letterSpacing: '-1px'}}>Personalized Playlists</h1>
					{/* Weather badge tanpa icon, warna badge menyesuaikan cuaca */}
					<div className="mt-2 mb-8">
						<WeatherBadge mood={weather.mood} city={weather.city} desc={weather.desc} />
					</div>
					{/* Cards di tengah bawah, jarak dari atas, responsif */}
					<div className="w-full flex justify-center mt-28 md:mt-40">
						<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 w-full max-w-5xl px-2 sm:px-8 md:px-12 lg:px-20 xl:px-0">
							{playlists.map((p) => (
								<PlaylistCard
									key={p.title}
									title={p.title}
									icon={p.icon}
									count={p.count}
									duration={p.duration}
									link={p.link}
									onClick={() => updatePlaylistAnalytics(p)}
								/>
							))}
						</div>
					</div>
				</div>
				{/* Keyframes & style untuk sun, cloud, glow, card hover, responsif grid */}
				<style>{`
					@keyframes sunPeek {
						0%,100%{transform:translateY(0)}
						50%{transform:translateY(12px)}
					}
					@keyframes cloudDrift {
						0%{left:-100px} 100%{left:0}
					}
					@keyframes glowPulse {
						0%,100%{opacity:0.7} 50%{opacity:1}
					}
					.card:hover {
						transform: scale(1.03);
						box-shadow: 0 12px 24px rgba(0,0,0,0.10);
						transition: all 0.3s ease;
					}
					@media (max-width: 1024px) {
						.card { min-width: 180px !important; max-width: 320px !important; }
					}
					@media (max-width: 768px) {
						.sun, .sun-glow { right: 24px !important; top: 32px !important; width: 64px !important; height: 64px !important; }
						.cloud { top: 0 !important; left: -40px !important; height: 120px !important; }
						.card { min-width: 140px !important; max-width: 98vw !important; }
					}
					@media (max-width: 480px) {
						.card { min-width: 90vw !important; max-width: 99vw !important; }
					}
				`}</style>
			</div>
		</>
	);
}

// WeatherBadge baru: tanpa icon, warna badge sesuai cuaca
function WeatherBadge({ mood, city, desc }: { mood: string; city: string; desc: string }) {
  // Map warna badge ke cuaca
  const badgeColor: Record<string, string> = {
    clear: '#fef9c3', // kuning muda
    cloudy: '#e0e7ef', // biru keabu
    rain: '#bae6fd', // biru muda
    snow: '#f1f5f9', // putih kebiruan
    fog: '#e5e7eb', // abu terang
    thunder: '#fef08a', // kuning petir
    night: '#c7d2fe', // biru malam
    extreme: '#fcd34d',
  };
  return (
    <span
      className="flex items-center gap-2 px-6 py-2 rounded-full shadow text-base font-semibold animate-fadeIn border"
      style={{ minWidth: 140, background: badgeColor[mood] || '#fef9c3', borderColor: '#e0d7d2', color: '#232634', fontWeight: 700 }}
    >
      <span className="font-bold">{city}</span>
      <span className="opacity-80">- {desc.charAt(0).toUpperCase() + desc.slice(1)}</span>
    </span>
  );
}
